import { useCallback, useState } from 'react';

import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

import {
  DataTableFiltersV2,
  TableFilterConfig,
  UseTableFiltersReturn,
} from '@/components/data-table/filters-v2';
import {
  DateRangePickerV2,
  DateRangePreset,
  DateRangeV2,
  getDateRangeFromPreset,
  useDateRangePickerV2,
} from '@/modules/common/components/molecules/date/date-range-picker-v2';
import { MultiSelectWithPagination } from '@/modules/common/components/molecules/selectors/multiselect-with-pagination';
import { getShortProjects } from '@/modules/projects/api/short-projects.api';
import { getShortTeamMembers } from '@/modules/team/api/team.api';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { DateFilter } from '../types';

const REPORT_FILTERS_TABLE_ID = 'report-builder-filters';
const PROJECTS_FILTER_QUERY_KEY: unknown[] = ['short-projects', 'reports-filter'];
const MEMBERS_FILTER_QUERY_KEY: unknown[] = ['short-members', 'reports-filter'];

interface ReportFilterBarProps {
  dateFilter: DateFilter;
  onFilterChange: (filter: DateFilter) => void;
  /** Show the inline Project / Member pickers — some element groups by that dimension */
  hasProjectDimension: boolean;
  hasMemberDimension: boolean;
  /** "Filter" dropdown: every other filter the report's data sources support */
  filtersConfig: TableFilterConfig[];
  filterState: UseTableFiltersReturn;
}

export const ReportFilterBar = ({
  dateFilter,
  onFilterChange,
  hasProjectDimension,
  hasMemberDimension,
  filtersConfig,
  filterState,
}: ReportFilterBarProps) => {
  const { t } = useTranslation('authenticated.reports');

  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(() =>
    (dateFilter.projectIds ?? []).map(String)
  );
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(() =>
    (dateFilter.memberIds ?? []).map(String)
  );

  const loadProjects = useCallback(
    (params: { searchString?: string; cursor: number | null }) =>
      getShortProjects({
        filter: params.searchString ? { searchString: params.searchString } : undefined,
        pagination: { cursor: params.cursor ?? undefined },
      }),
    []
  );

  const loadMembers = useCallback(
    (params: { searchString?: string; cursor: number | null }) =>
      getShortTeamMembers({
        filter: params.searchString ? { searchString: params.searchString } : undefined,
        pagination: { cursor: params.cursor ?? undefined },
      }),
    []
  );

  const handleProjectValueChange = (ids: string[]) => {
    setSelectedProjectIds(ids);
    onFilterChange({ ...dateFilter, projectIds: ids.map(Number) });
  };

  const handleMemberValueChange = (ids: string[]) => {
    setSelectedMemberIds(ids);
    onFilterChange({ ...dateFilter, memberIds: ids.map(Number) });
  };

  const initialDates: DateRangeV2 | null =
    dateFilter.dateFrom && dateFilter.dateTo
      ? { startDate: parseISO(dateFilter.dateFrom), endDate: parseISO(dateFilter.dateTo) }
      : null;

  const {
    dates,
    selectedPreset,
    onChange: onDatesChange,
    onPresetSelect,
  } = useDateRangePickerV2({
    defaultValue: initialDates ?? DateRangePreset.ThisMonth,
  });

  const handleDateRangeChange = (newDates: DateRangeV2) => {
    onDatesChange(newDates);
    onFilterChange({
      ...dateFilter,
      dateFrom: format(normalizeToStartOfDayUTC(newDates.startDate), 'yyyy-MM-dd'),
      dateTo: format(normalizeToStartOfDayUTC(newDates.endDate), 'yyyy-MM-dd'),
    });
  };

  const handlePresetSelect = (preset: DateRangePreset) => {
    onPresetSelect(preset);
    const newDates = getDateRangeFromPreset(preset);
    onFilterChange({
      ...dateFilter,
      dateFrom: format(normalizeToStartOfDayUTC(newDates.startDate), 'yyyy-MM-dd'),
      dateTo: format(normalizeToStartOfDayUTC(newDates.endDate), 'yyyy-MM-dd'),
    });
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 py-1">
      <DateRangePickerV2
        dates={dates}
        onChange={handleDateRangeChange}
        selectedPreset={selectedPreset}
        onPresetSelect={handlePresetSelect}
        defaultPrefix={t('builder.dateRange')}
        align="start"
      />

      {hasProjectDimension && (
        <MultiSelectWithPagination
          queryKey={PROJECTS_FILTER_QUERY_KEY}
          loadItems={loadProjects}
          value={selectedProjectIds}
          onValueChange={handleProjectValueChange}
          placeholder={t('panel.filterProjects')}
          maxCount={2}
          className="w-auto min-w-36"
        />
      )}

      {hasMemberDimension && (
        <MultiSelectWithPagination
          queryKey={MEMBERS_FILTER_QUERY_KEY}
          loadItems={loadMembers}
          value={selectedMemberIds}
          onValueChange={handleMemberValueChange}
          placeholder={t('panel.filterMembers')}
          maxCount={2}
          className="w-auto min-w-36"
        />
      )}

      {filtersConfig.length > 0 && (
        <DataTableFiltersV2
          filtersConfig={filtersConfig}
          filterState={filterState}
          tableId={REPORT_FILTERS_TABLE_ID}
          showSaveButton={false}
        />
      )}
    </div>
  );
};
